// The landing IS the garage: a full-screen customization bay in the style of
// a racing game's tuning menu. The two questions (carrier, location) are menu
// rows; each row swings the camera to the part of the car it "tunes" (the
// rear plate shows the ZIP), and "See repair shops" — locked until both are
// set — rolls the door up and drives the car out onto the shop map.
//
// Everything here is a real button (the 3D scene is decoration, never the
// only way to do something): arrow keys move the highlight, Enter chooses,
// Esc/Backspace backs out of a sub-menu.
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Insurer } from "../shared/types";
import { CAR_COLORS } from "./GarageCar";
import { GarageBoundary, useIdleReady, webglAvailable } from "./GarageView";
import { SPECIAL_IDS, partitionForPicker } from "./insurers";
import type { DriveHooks, GarageApi, ShotName, StageRect } from "./garage/engine";

const Garage = lazy(() => import("./garage/Garage"));

export interface LandingStats {
  repair: number;
  network: number | null;
  tow: number;
  salvage: number;
  nearest: number | null;
}

export interface LandingLoc {
  zip: string | null;
  coords: { lat: number; lng: number } | null;
  decided: boolean;
}

type Menu = "main" | "insurer" | "loc";

interface Row {
  id: string;
  label: string;
  value?: string;
  state?: "done" | "locked" | "go" | "sel";
}

interface Info {
  tag: string;
  title: string;
  body: string;
  locked?: boolean;
  /** Must stay readable even where the compact phone sheet hides the card. */
  alert?: boolean;
}

export function Landing({
  visible,
  driving,
  driveHooks,
  onLive,
  insurers,
  insurersErr,
  onRetryInsurers,
  insurerId,
  onInsurer,
  loc,
  onLoc,
  stats,
  statsLoading,
  openPanel,
  notice,
  onGo,
}: {
  visible: boolean;
  driving: boolean;
  driveHooks: DriveHooks;
  onLive: (live: boolean) => void;
  insurers: Insurer[];
  insurersErr: boolean;
  onRetryInsurers: () => void;
  insurerId: string | null;
  onInsurer: (id: string) => void;
  loc: LandingLoc;
  onLoc: (next: { zip: string | null; coords: { lat: number; lng: number } | null }) => void;
  stats: LandingStats | null;
  statsLoading: boolean;
  openPanel: { panel: "insurer" | "loc"; nonce: number } | null;
  notice: string | null;
  onGo: () => void;
}) {
  const [webgl] = useState(webglAvailable);
  const idle = useIdleReady();
  const [live, setLive] = useState(false);
  const [failed, setFailed] = useState(false);
  const api = useRef<GarageApi | null>(null);

  const [menu, setMenu] = useState<Menu>("main");
  const [hi, setHi] = useState(0);
  const [zipOpen, setZipOpen] = useState(false);
  const [zipInput, setZipInput] = useState(loc.zip ?? "");
  const [geo, setGeo] = useState<"idle" | "asking" | "failed">("idle");
  const [shake, setShake] = useState(0);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const leftRef = useRef<HTMLDivElement | null>(null);
  // Phones: the menu is a bottom sheet, so the car is framed in the space
  // above it. Re-measured whenever the sheet changes size (the carrier list
  // makes it taller); the drive-out hands the whole screen back.
  const [stage, setStage] = useState<StageRect | null>(null);
  const zipRef = useRef<HTMLInputElement | null>(null);

  const insurer = insurers.find((i) => i.id === insurerId) ?? null;
  const unlocked = Boolean(insurerId) && loc.decided;
  const locLabel = loc.coords ? "My location" : loc.zip ? `ZIP ${loc.zip}` : loc.decided ? "Skipped" : "";

  const { carriers, special } = useMemo(() => partitionForPicker(insurers), [insurers]);
  const pickList = useMemo(() => [...carriers, ...special], [carriers, special]);

  const rows: Row[] = useMemo(() => {
    if (menu === "insurer") {
      if (insurersErr) return [{ id: "retry", label: "Couldn't load carriers", value: "Try again" }];
      if (pickList.length === 0) return [{ id: "loading", label: "Loading carriers…" }];
      return pickList.map((i) => ({
        id: i.id,
        label: i.name,
        value: i.id === insurerId ? "✓" : "",
        state: i.id === insurerId ? "sel" : undefined,
      }));
    }
    if (menu === "loc") {
      return [
        {
          id: "geo",
          label: "Use my current location",
          value: geo === "asking" ? "Locating…" : loc.coords ? "✓" : "GPS",
          state: loc.coords ? "sel" : undefined,
        },
        {
          id: "zip",
          label: "Enter a ZIP code",
          value: loc.zip ?? "",
          state: loc.zip ? "sel" : undefined,
        },
        {
          id: "skip",
          label: "Skip, list every shop",
          value: loc.decided && !loc.zip && !loc.coords ? "✓" : "",
        },
      ];
    }
    return [
      {
        id: "insurer",
        label: "Insurance",
        value: insurer?.name ?? "Choose",
        state: insurerId ? "done" : undefined,
      },
      { id: "loc", label: "Location", value: locLabel || "Choose", state: loc.decided ? "done" : undefined },
      {
        id: "go",
        label: "See repair shops",
        value: unlocked ? "GO" : "Locked",
        state: unlocked ? "go" : "locked",
      },
    ];
  }, [menu, insurersErr, pickList, insurerId, insurer, geo, loc, locLabel, unlocked]);

  const hiRow = rows[Math.min(hi, rows.length - 1)];

  // Camera follows the highlighted row: carrier = hero 3/4, location = the
  // rear plate (it shows the ZIP), go = the door.
  const shot: ShotName =
    menu === "insurer"
      ? "hero"
      : menu === "loc"
        ? "plate"
        : hiRow?.id === "loc"
          ? "plate"
          : hiRow?.id === "go"
            ? "door"
            : "hero";

  const info: Info = (() => {
    if (geo === "failed" && menu === "loc")
      return {
        alert: true,
        tag: "Location",
        title: "Couldn't get your location",
        body: "Your browser didn't share it. Enter a ZIP code instead and we'll sort shops by distance.",
      };
    if (menu === "insurer") {
      const i = pickList.find((p) => p.id === hiRow?.id);
      if (!i) return { tag: "Insurance", title: "Your carrier", body: "Loading the carrier list." };
      if (i.id === "uninsured")
        return {
          tag: "Insurance",
          title: i.name,
          body: "No network to filter by: you'll see every nearby shop, and any qualified shop can do the repair.",
        };
      if (SPECIAL_IDS.has(i.id))
        return {
          tag: "Insurance",
          title: i.name,
          body: "You'll see every nearby shop. Any qualified collision shop can repair your car.",
        };
      return {
        tag: "Insurance",
        title: i.name,
        body: "Shops in this carrier's published repair network get marked on the map. You can always switch to all shops.",
      };
    }
    if (menu === "loc") {
      if (hiRow?.id === "zip")
        return { tag: "Location", title: "ZIP code", body: "Five digits. We sort shops from that ZIP's center." };
      if (hiRow?.id === "skip")
        return {
          tag: "Location",
          title: "No location",
          body: "See every shop we list, sorted by name instead of distance.",
        };
      return {
        tag: "Location",
        title: "Device location",
        body: "Your browser asks first. Rounded to about 100 m, used only to sort shops, and never stored.",
      };
    }
    if (hiRow?.id === "insurer")
      return {
        tag: "Insurance",
        title: "Your carrier's network",
        body: "We'll mark repair shops in your carrier's published network. Not listed? Pick “Other / not listed” to see every nearby shop.",
      };
    if (hiRow?.id === "loc")
      return {
        tag: "Location",
        title: "Sort by distance",
        body: "Your device location or a ZIP code puts the nearest shops first. It isn't stored.",
      };
    return unlocked
      ? {
          tag: "Ready",
          title: "Drive out to the map",
          body: "Repair shops, tow trucks and junk yards near you, on a live map.",
        }
      : {
          tag: "Locked",
          title: "See repair shops",
          body: "Set your insurance and location to unlock the map.",
          locked: true,
        };
  })();

  // ---- navigation
  const enter = useCallback((m: Menu) => {
    setMenu(m);
    setZipOpen(false);
    setHi(0);
  }, []);

  const back = useCallback(() => {
    if (zipOpen) {
      setZipOpen(false);
      return;
    }
    if (menu !== "main") {
      setHi(menu === "insurer" ? 0 : 1);
      setMenu("main");
    }
  }, [menu, zipOpen]);

  // Map "change" links reopen the matching sub-menu.
  useEffect(() => {
    if (openPanel) enter(openPanel.panel);
  }, [openPanel, enter]);

  // Entering a sub-menu highlights the current choice.
  useEffect(() => {
    if (menu === "insurer" && insurerId) {
      const idx = pickList.findIndex((p) => p.id === insurerId);
      if (idx >= 0) setHi(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, pickList.length]);

  const askLocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setGeo("failed");
      setHi(1);
      setZipOpen(true);
      return;
    }
    setGeo("asking");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // Round to ~3 decimals (~110 m): enough to sort shops by distance,
        // never more precise than we need.
        onLoc({
          zip: null,
          coords: {
            lat: Math.round(pos.coords.latitude * 1000) / 1000,
            lng: Math.round(pos.coords.longitude * 1000) / 1000,
          },
        });
        setGeo("idle");
        setMenu("main");
        setHi(2);
      },
      // Denied, unavailable, or timed out — fall back to the ZIP field.
      () => {
        setGeo("failed");
        setHi(1);
        setZipOpen(true);
      },
      { timeout: 10000, maximumAge: 60000 },
    );
  }, [onLoc]);

  const saveZip = useCallback(() => {
    if (!/^\d{5}$/.test(zipInput)) return;
    onLoc({ zip: zipInput, coords: null });
    setZipOpen(false);
    setGeo("idle");
    setMenu("main");
    setHi(2);
  }, [zipInput, onLoc]);

  const activate = useCallback(
    (row: Row | undefined) => {
      if (!row) return;
      if (menu === "main") {
        if (row.id === "insurer") enter("insurer");
        else if (row.id === "loc") enter("loc");
        else if (row.id === "go") {
          if (unlocked) onGo();
          else setShake((n) => n + 1);
        }
        return;
      }
      if (menu === "insurer") {
        if (row.id === "retry") return onRetryInsurers();
        if (row.id === "loading") return;
        onInsurer(row.id);
        setMenu("main");
        setHi(loc.decided ? 2 : 1);
        return;
      }
      if (row.id === "geo") askLocation();
      else if (row.id === "zip") {
        setZipOpen(true);
        setTimeout(() => zipRef.current?.focus(), 0);
      } else if (row.id === "skip") {
        onLoc({ zip: null, coords: null });
        setMenu("main");
        setHi(2);
      }
    },
    [menu, unlocked, onGo, enter, onRetryInsurers, onInsurer, loc.decided, askLocation, onLoc],
  );

  // Keyboard: game-menu controls on top of the native button behavior.
  useEffect(() => {
    if (!visible || driving) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = t?.tagName === "INPUT";
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (typing) return;
        e.preventDefault();
        setHi((h) => {
          const n = rows.length;
          const next = (Math.min(h, n - 1) + (e.key === "ArrowDown" ? 1 : n - 1)) % n;
          setTimeout(() => rowRefs.current[next]?.focus(), 0);
          return next;
        });
      } else if (e.key === "Escape" || (e.key === "Backspace" && !typing)) {
        e.preventDefault();
        back();
      } else if (e.key === "Enter" && !typing && !(t instanceof HTMLButtonElement)) {
        e.preventDefault();
        activate(hiRow);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, driving, rows.length, back, activate, hiRow]);

  // ---- drive-out
  useEffect(() => {
    if (!driving) return;
    const a = api.current;
    if (a && live) a.driveOut(driveHooks);
    else {
      driveHooks.onReveal?.();
      driveHooks.onArrive?.();
      driveHooks.onDone?.();
    }
    // Hooks are stable for the life of a drive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driving]);

  // Back from the map: car home, door down, menu on the drive-out row.
  const wasVisible = useRef(visible);
  useEffect(() => {
    if (visible && !wasVisible.current) {
      api.current?.reset();
      if (!openPanel) {
        setMenu("main");
        setHi(unlocked ? 2 : 0);
      }
    }
    wasVisible.current = visible;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => onLive(live && !failed), [live, failed, onLive]);

  useEffect(() => {
    const root = rootRef.current;
    const sheet = leftRef.current;
    if (!root || !sheet) return;
    // Same breakpoints as the CSS: portrait = bottom sheet (stage above it);
    // short landscape = slim left column (stage to its right).
    const portrait = window.matchMedia("(max-aspect-ratio: 9/10)");
    const shortLandscape = window.matchMedia("(min-aspect-ratio: 9/10) and (max-height: 560px)");
    const same = (a: StageRect | null, b: StageRect) =>
      a !== null && Math.abs(a.x - b.x) + Math.abs(a.y - b.y) + Math.abs(a.w - b.w) + Math.abs(a.h - b.h) < 2;
    const measure = () => {
      const r = root.getBoundingClientRect();
      const m = sheet.getBoundingClientRect();
      let next: StageRect | null = null;
      if (!driving && r.width > 0) {
        if (portrait.matches) {
          next = { x: 0, y: 0, w: r.width, h: Math.max(140, Math.round(m.top - r.top - 4)) };
        } else if (shortLandscape.matches) {
          const x = Math.round(m.right - r.left + 8);
          next = { x, y: 0, w: Math.max(160, r.width - x), h: r.height };
        }
      }
      if (next && portrait.matches) root.style.setProperty("--stage-h", `${next.h}px`);
      else root.style.removeProperty("--stage-h");
      setStage((prev) => (next === null ? null : same(prev, next) ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    ro.observe(sheet);
    portrait.addEventListener("change", measure);
    shortLandscape.addEventListener("change", measure);
    return () => {
      ro.disconnect();
      portrait.removeEventListener("change", measure);
      shortLandscape.removeEventListener("change", measure);
    };
  }, [driving, visible]);

  const car = useMemo(
    () => ({
      color: CAR_COLORS[0],
      insurerName: insurerId && !SPECIAL_IDS.has(insurerId) ? insurer?.name : undefined,
      zip: loc.zip ?? undefined,
      plate: loc.zip ?? (loc.coords ? "GPS" : undefined),
      collisionType: "",
      impactPoints: [],
      symptoms: [],
    }),
    [insurerId, insurer, loc.zip, loc.coords],
  );

  const barTitle = menu === "insurer" ? "Insurance carrier" : menu === "loc" ? "Location" : "Find a repair shop";
  const doneCount = (insurerId ? 1 : 0) + (loc.decided ? 1 : 0);
  const counter = menu === "main" ? `${doneCount} / 2` : `${Math.min(hi, rows.length - 1) + 1} / ${rows.length}`;

  return (
    <div
      ref={rootRef}
      className={`lc${driving ? " driving" : ""}${live && !failed ? " live" : ""}${visible ? "" : " away"}`}
      aria-hidden={!visible}
    >
      <div className="lc-stage">
        <picture>
          <source media="(max-aspect-ratio: 9/10)" srcSet="/garage/poster-stage.webp" />
          <img className="lc-poster" src="/garage/poster-wide.webp" alt="" fetchPriority="high" />
        </picture>
        {webgl && idle && !failed && (
          <GarageBoundary fallback={null}>
            <Suspense fallback={null}>
              <Garage
                car={car}
                variant="hero"
                shot={shot}
                stage={stage}
                apiRef={api}
                active={visible}
                onReady={() => setLive(true)}
                onError={() => setFailed(true)}
                label={`Your car in a 3D garage${car.insurerName ? `, insured by ${car.insurerName}` : ""}`}
              />
            </Suspense>
          </GarageBoundary>
        )}
      </div>
      <div className="lc-shade" aria-hidden="true" />

      {notice && <p className="lc-notice">{notice}</p>}

      <div className="lc-left" ref={leftRef}>
      <section className="lc-menu" aria-label="Find a repair shop">
        <header className="lc-brand">
          <span className="lc-brand-t">Garage</span>
          <span className="lc-brand-s">Free &amp; unbiased · no sign-up</span>
        </header>
        <div className="lc-bar">
          {menu !== "main" ? (
            <button className="lc-back" onClick={back} aria-label="Back">
              ‹ {barTitle}
            </button>
          ) : (
            <span>{barTitle}</span>
          )}
          <span className="lc-count">{counter}</span>
        </div>
        <div className={`lc-list${menu === "insurer" ? " long" : ""}`} role="menu" aria-label={barTitle}>
          {rows.map((r, i) => {
            const isHi = i === Math.min(hi, rows.length - 1);
            if (menu === "loc" && r.id === "zip" && zipOpen) {
              const ok = /^\d{5}$/.test(zipInput);
              return (
                <form
                  key={r.id}
                  className="lc-row lc-zip hi"
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveZip();
                  }}
                >
                  <input
                    ref={zipRef}
                    className="lc-zip-in"
                    placeholder="ZIP code"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    aria-label="ZIP code"
                    value={zipInput}
                    onChange={(e) => setZipInput(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  />
                  <button className="lc-zip-go" disabled={!ok}>
                    Save
                  </button>
                </form>
              );
            }
            return (
              <button
                key={r.id}
                ref={(el) => {
                  rowRefs.current[i] = el;
                }}
                role="menuitem"
                tabIndex={isHi ? 0 : -1}
                aria-disabled={r.state === "locked" || undefined}
                className={`lc-row${isHi ? " hi" : ""}${r.state ? ` ${r.state}` : ""}${
                  r.state === "locked" && shake ? ` shake${shake % 2}` : ""
                }`}
                onMouseEnter={() => setHi(i)}
                onFocus={() => setHi(i)}
                onClick={() => activate(r)}
              >
                <span className="lc-row-l">
                  {r.state === "done" && <i className="lc-check" aria-hidden="true" />}
                  {r.label}
                </span>
                <span className="lc-row-v">
                  {r.state === "locked" && <i className="lc-lock" aria-hidden="true" />}
                  {r.value}
                  {r.state === "go" && <i className="lc-arrow" aria-hidden="true" />}
                </span>
              </button>
            );
          })}
        </div>
        <div className="lc-more" aria-hidden="true" />
      </section>

      <aside
        className={`lc-card${info.locked ? " locked" : ""}${info.alert ? " alert" : ""}`}
        aria-live="polite"
      >
        <div className="lc-card-tag">
          {info.locked && <i className="lc-lock" aria-hidden="true" />}
          {info.tag}
        </div>
        <div className="lc-card-t">{info.title}</div>
        <p className="lc-card-b">{info.body}</p>
      </aside>

      <div className="lc-stats" aria-label="Your area">
        <div className="lc-stats-h">
          <span>Area scan</span>
          <span>
            {statsLoading
              ? "Scanning…"
              : stats?.nearest != null
                ? `Nearest ${stats.nearest} mi`
                : stats
                  ? "By name"
                  : "Set both to scan"}
          </span>
        </div>
        {[
          { label: "Repair shops", v: stats?.repair ?? null, max: 30 },
          {
            label: insurer && !SPECIAL_IDS.has(insurer.id) ? `${insurer.name} network` : "In network",
            v: stats ? stats.network : null,
            max: Math.max(1, stats?.repair ?? 1),
          },
          { label: "Tow trucks", v: stats?.tow ?? null, max: 20 },
          { label: "Junk yards", v: stats?.salvage ?? null, max: 10 },
        ].map((b) => (
          <div className="lc-stat" key={b.label}>
            <span className="lc-stat-l">{b.label}</span>
            <span className={`lc-stat-t${statsLoading ? " scan" : ""}`}>
              <span className="lc-stat-f" style={{ width: `${Math.min(1, (b.v ?? 0) / b.max) * 100}%` }} />
            </span>
            <span className="lc-stat-v">{b.v ?? "—"}</span>
          </div>
        ))}
      </div>

      </div>

      <div className="lc-keys" aria-hidden="true">
        <span>
          <kbd>↑</kbd>
          <kbd>↓</kbd> Select
        </span>
        <span>
          <kbd>Enter</kbd> Choose
        </span>
        <span>
          <kbd>Esc</kbd> Back
        </span>
        <span>
          <kbd>Drag</kbd> Rotate
        </span>
      </div>

      {driving && (
        <button className="lc-skip" onClick={() => api.current?.finishDrive()}>
          Skip ▸▸
        </button>
      )}
    </div>
  );
}
